import dayjs from "dayjs";
import { type IUser } from "src/components/shared/UserList/UserList.types";

import {
  type IFgasField,
  type IFgasGwpData,
  type IFgasGwpItem,
  type IFgasLegalStatus,
} from "./Fgas.types";

export const legalStatusFields: IFgasField<IFgasLegalStatus>[] = [
  { name: "operator", label: "Legal Operator" },
  { name: "operator_address", label: "Address", multiline: true },
  { name: "company", label: "Certified Installing / Maintenance Company" },
  { name: "company_address", label: "Address", multiline: true },
  { name: "certification_body", label: "F-GAS Certifying Body" },
  { name: "certification_number", label: "F-GAS Certificate Unique Number" },
];

export const gwpFields: IFgasField<IFgasGwpItem>[] = [
  { name: "refrigerant", label: "Refrigerant" },
  { name: "other_name", label: "Other Name" },
  { name: "gwp", label: "GWP" },
  { name: "Atonnes", label: "5 t" },
  { name: "Btonnes", label: "10 t" },
  { name: "Ctonnes", label: "50 t" },
  { name: "Dtonnes", label: "500 t" },
];

export const emptyLegalStatus = (): IFgasLegalStatus => ({
  id: null,
  operator: "",
  operator_address: "",
  company: "",
  company_address: "",
  certification_body: "",
  certification_number: "",
  expiry_date: null,
});

export const emptyGwpItem = (): IFgasGwpItem => ({
  id: null,
  refrigerant: "",
  other_name: "",
  gwp: "",
  Atonnes: "",
  Btonnes: "",
  Ctonnes: "",
  Dtonnes: "",
  active: 1,
});

const getList = (data: any): any[] =>
  Array.isArray(data?.list) ? data.list : [];

export const mapLegalStatus = (data: any): IFgasLegalStatus => {
  const [item] = getList(data);
  return item ? { ...emptyLegalStatus(), ...item } : emptyLegalStatus();
};

export const mapGwp = (data: any): IFgasGwpData => {
  const list: IFgasGwpItem[] = getList(data).map(el => ({
    ...emptyGwpItem(),
    ...el,
    active: Number(el.active ?? 1),
  }));
  return {
    active: list.filter(el => el.active === 1),
    archived: list.filter(el => el.active !== 1),
  };
};

export const mapFgasHistoryRow = (users: IUser[]) => (row: any) => {
  const user = users.find(el => el.userid === row.userid);
  return {
    ...row,
    id: row.hid,
    detail: [user?.rolename, user?.full_name].filter(Boolean).join(" "),
    date: row.timestamp,
    dateUTC: row.timestampUTC,
  };
};

export const mapLegalStatusSnapshot = (row: any): IFgasLegalStatus => ({
  ...emptyLegalStatus(),
  ...row?.data,
});

export const mapGwpSnapshot = (row: any): IFgasGwpItem[] => {
  const data: any[] = Array.isArray(row?.data) ? row.data : [];
  const list = data.map(el => ({
    ...el,
    Atonnes: el["5tonnes"],
    Btonnes: el["10tonnes"],
    Ctonnes: el["50tonnes"],
    Dtonnes: el["500tonnes"],
  }));
  return mapGwp({ list }).active;
};

const formatDate = (value: unknown) => {
  if (!value) return null;
  const date = dayjs(value as string | Date);
  return date.isValid() ? date.format("YYYY-MM-DD HH:mm:ss") : null;
};

export const toLegalStatusPayload = (
  item: IFgasLegalStatus,
): IFgasLegalStatus => ({
  ...item,
  expiry_date: formatDate(item.expiry_date),
});
